import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import ParallaxHeader from '../components/Parallaxheader';
import { CheckCircle, Clock, Users, Calendar, Award, BookOpen, CreditCard } from 'lucide-react';

const LegalPages = () => {
  const { page } = useParams();

  // Redirect support to contact
  if (page === 'support') {
    return <Navigate to="/contact" replace />;
  }

  // Content for each page
  const pageContent = {
    'privacy-policy': {
      title: 'Privacy Policy',
      breadcrumb: 'Privacy Policy',
      content: (
        <div className="prose prose-lg max-w-none">
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="text-gray-600 mb-3">
                At Thirumal Chess Academy, we collect information to provide better services to our students and tournament participants.
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Personal Information:</strong> Name, email, phone number, address when you register for courses or tournaments</li>
                <li><strong>Student Information:</strong> Age, grade level, chess rating, progress reports</li>
                <li><strong>Payment Information:</strong> Transaction details (processed securely through third-party payment gateways)</li>
                <li><strong>Technical Information:</strong> IP address, browser type, device information for website analytics</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>To provide and maintain our chess coaching services</li>
                <li>To process tournament registrations and payments</li>
                <li>To communicate important updates, schedule changes, and announcements</li>
                <li>To improve our teaching methods and curriculum</li>
                <li>To comply with legal obligations under Indian law</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Data Protection</h2>
              <p className="text-gray-600 mb-3">
                We implement appropriate security measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Secure SSL encryption for all data transmission</li>
                <li>Restricted access to personal data for authorized personnel only</li>
                <li>Regular security audits and updates</li>
                <li>Compliance with the Information Technology Act, 2000 and its amendments</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Your Rights</h2>
              <p className="text-gray-600 mb-3">
                Under applicable laws, you have the right to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent for data processing</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Contact Information</h2>
              <p className="text-gray-600">
                For privacy-related questions or requests, contact our Data Protection Officer at:
                <br />
                <strong>Email:</strong> kalyanraghavanchess@gmail.com
                <br />
                <strong>Phone:</strong> +91-XXXXXXXXXX
                <br />
                <strong>Address:</strong> Thirumal Chess Academy, Tambaram, Chennai, Tamil Nadu
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3">Policy Updates</h3>
              <p className="text-blue-800 text-sm">
                This privacy policy was last updated on {new Date().toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric' 
                })}. We may update this policy periodically.
              </p>
            </div>
          </div>
        </div>
      )
    },
    'terms-and-conditions': {
      title: 'Terms and Conditions',
      breadcrumb: 'Terms and Conditions',
      content: (
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600">
              By enrolling in Thirumal Chess Academy's programs, you agree to be bound by these Terms and Conditions. 
              These terms govern our student-teacher relationship and all services provided.
            </p>
          </div>

          {/* Course Details Section */}
          <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-emerald-600" />
              Course Details & Policies
            </h2>
            
            <div className="space-y-6">
              {/* Fee Structure */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  <h3 className="text-lg font-bold text-gray-900">Fee Structure</h3>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-gray-700"><strong>Monthly Fee:</strong> ₹1000 per month</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span className="text-gray-700"><strong>Payment Due:</strong> First week of every month</span>
                </div>
              </div>

              {/* Class Schedule */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-bold text-gray-900">Class Schedule</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-gray-700"><strong>Frequency:</strong> Thrice a week</p>
                      <p className="text-gray-600 text-sm">1 hour per session</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-gray-700"><strong>Batch Options:</strong></p>
                      <ul className="list-disc pl-5 text-gray-600 mt-1 space-y-1">
                        <li>Monday, Wednesday, Friday</li>
                        <li>Tuesday, Thursday, Saturday</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Demo Session */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-bold text-gray-900">Demo Session</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700">Free of cost</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700">One-on-one sessions available only on Saturday/Sunday</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-gray-700">Schedule provided only after demo class & student evaluation</span>
                  </div>
                </div>
              </div>

              {/* Class Format */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Users className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">Class Format</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                    <span className="text-gray-700">Both online and offline classes available</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                    <span className="text-gray-700">Group class & One-on-One sessions available</span>
                  </div>
                </div>
              </div>

              {/* Special Training */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Award className="w-5 h-5 text-amber-600" />
                  <h3 className="text-lg font-bold text-gray-900">Special Training for Rated Players</h3>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-2"></div>
                  <span className="text-gray-700">Intensive training available every day for players with national/international chess rating</span>
                </div>
              </div>

              {/* Compensation Policy */}
              <div className="bg-white rounded-lg p-5 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-bold text-gray-900">Compensation & Attendance Policy</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <span className="text-gray-700">If the trainer is on leave, a compensation class will be provided</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                    <span className="text-gray-700">If a student is absent, a makeup session will not be offered</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Standard Terms */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Registration and Accounts</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>You must be at least 18 years old to create an account, or have parental consent if under 18</li>
              <li>You are responsible for maintaining the confidentiality of your account credentials</li>
              <li>You must provide accurate and complete information during registration</li>
              <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Fees and Payments</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Course fees must be paid in advance as per the specified payment schedule</li>
              <li>Tournament registration fees are non-refundable except in cases of event cancellation by the academy</li>
              <li>We accept payments through secure third-party gateways (Razorpay, PayPal, bank transfer, UPI)</li>
              <li>All fees are in Indian Rupees (INR) unless otherwise specified</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Code of Conduct</h2>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Respect for coaches, staff, and fellow students is mandatory</li>
              <li>Strict adherence to FIDE rules and anti-cheating policies</li>
              <li>Appropriate behavior during classes and tournaments</li>
              <li>No harassment, discrimination, or inappropriate conduct will be tolerated</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Governing Law</h2>
            <p className="text-gray-600">
              These terms shall be governed by and construed in accordance with the laws of India. 
              Any disputes shall be subject to the exclusive jurisdiction of the courts in Chennai, Tamil Nadu.
            </p>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Contact for Queries</h3>
            <p className="text-gray-700">
              For questions about these Terms and Conditions, please contact:
              <br />
              <strong>Email:</strong> kalyanraghavanchess@gmail.com
              <br />
              <strong>Phone:</strong> +91-XXXXXXXXXX
              <br />
              <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-IN')}
            </p>
          </div>
        </div>
      )
    },
    'faq': {
      title: 'Frequently Asked Questions',
      breadcrumb: 'FAQ',
      content: (
        <>
          <div className="space-y-8">
            {/* Course & Fee Questions */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg">
                    <CreditCard className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold">Course & Fee Details</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">What is the course fee?</h3>
                    <p className="text-gray-700">The monthly fee is ₹1000 per month, payable in the first week of each month.</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">How many classes per week?</h3>
                    <p className="text-gray-700">Classes are held thrice a week, with each session lasting 1 hour.</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">What batch timings are available?</h3>
                    <p className="text-gray-700">You can choose between Monday-Wednesday-Friday or Tuesday-Thursday-Saturday batches.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Demo & Admission */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg">
                    <CheckCircle className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold">Demo & Admission Process</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">Is there a demo session available?</h3>
                    <p className="text-gray-700">Yes! We offer a free demo session. One-on-one demo sessions are available only on Saturdays and Sundays.</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">When will I get my class schedule?</h3>
                    <p className="text-gray-700">Your class schedule will be provided only after the demo class and evaluation of the student's skill level.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Class Format */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg">
                    <Users className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold">Class Format & Options</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">Are online classes available?</h3>
                    <p className="text-gray-700">Yes, we offer both online and offline classes to suit your convenience.</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">Can I get one-on-one coaching?</h3>
                    <p className="text-gray-700">Yes, we offer both group classes and one-on-one sessions based on your preference.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Special Training */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-amber-500 to-amber-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg">
                    <Award className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold">Special Training Programs</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">Do you offer special training for rated players?</h3>
                    <p className="text-gray-700">Yes, we provide intensive daily training for players with national or international chess ratings.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Policies */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
              <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
                <div className="flex items-center gap-4">
                  <div className="bg-white/20 p-3 rounded-lg">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold">Attendance & Compensation Policy</h2>
                </div>
              </div>
              
              <div className="p-6">
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">What if the trainer is on leave?</h3>
                    <p className="text-gray-700">If the trainer is on leave, a compensation class will be provided at a mutually convenient time.</p>
                  </div>
                  
                  <div className="border border-gray-200 rounded-lg p-6">
                    <h3 className="font-bold text-gray-900 mb-2">What if I miss a class?</h3>
                    <p className="text-gray-700">If a student is absent, a makeup session will not be offered. Please refer to our compensation policy.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Support */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Need More Information?</h3>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Can't find what you're looking for? Contact our support team for personalized assistance regarding courses, fees, and schedules.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-lg transition-colors inline-flex items-center justify-center gap-2"
              >
                Contact Us
              </a>
              <a
                href="tel:+91XXXXXXXXXX"
                className="bg-white hover:bg-gray-100 text-blue-600 font-medium py-3 px-8 rounded-lg border border-blue-600 transition-colors inline-flex items-center justify-center gap-2"
              >
                Call Now
              </a>
            </div>
          </div>
        </>
      )
    }
  };

  // Get the current page content or show 404
  const currentPage = pageContent[page];

  if (!currentPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404 - Page Not Found</h1>
          <p className="text-gray-600 mb-8">The page you're looking for doesn't exist.</p>
          <a href="/" className="text-blue-600 hover:text-blue-800 font-medium">
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <ParallaxHeader
        title={currentPage.title}
        breadcrumbs={[{ title: currentPage.breadcrumb, path: `/${page}` }]}
        backgroundImage="/images/aboutheader.jpg"
        overlayColor="rgba(0, 0, 0, 0.7)"
      />
      
      <section className="py-20 bg-gradient-to-b from-white to-[#f8f5ed]">
        <div className={`max-w-${page === 'faq' ? '6xl' : '4xl'} mx-auto px-4 sm:px-6 lg:px-8`}>
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
            {currentPage.content}
          </div>
        </div>
      </section>
    </>
  );
};

export default LegalPages;